export const DATA_MODEL_CATALOG = {
  bert_legacy_features: {
    id: 'bert_legacy_features',
    label: 'Legacy BERT features',
    description: 'Context-sensitive passage features labeled as BERT in the legacy dataset.',
    calculation: 'The collection stores three values per passage, but the unavailable generation pipeline does not document its BERT checkpoint, tokenization, pooling, or dimensionality-reduction steps. These values must therefore be treated as an undocumented preview rather than a reproducible semantic model.',
  },
  gensim_lda_10_legacy: {
    id: 'gensim_lda_10_legacy',
    label: 'Gensim LDA · 10 topics',
    description: 'Each passage is represented as a probability mixture over ten learned word topics.',
    calculation: 'Gensim LDA starts with bag-of-words counts and estimates latent word topics with online variational Bayes. The legacy collection stores ten topic probabilities for each passage, but the external pipeline and its preprocessing parameters are not present in this repository.',
  },
  tfidf_lexical_v1: {
    id: 'tfidf_lexical_v1',
    label: 'Sublinear TF-IDF lexical vectors',
    description: 'Each passage is represented by normalized weights for the words that are distinctive within this corpus.',
    calculation: 'ScriptureScope tokenizes normalized lowercase text, removes a fixed function-word list, uses sublinear term frequency and smoothed inverse document frequency, drops terms found in fewer than two or more than 80 percent of passages, and L2-normalizes the resulting sparse vectors.',
  },
  bertopic_semantic_affinity_v1: {
    id: 'bertopic_semantic_affinity_v1',
    label: 'BERTopic semantic affinities',
    description: 'Each BSB passage is represented by transformer-derived semantic affinities across 122 reviewed BERTopic topics.',
    calculation: 'Passage embeddings from all-MiniLM-L6-v2 are clustered with seeded UMAP and HDBSCAN. Stored topic distributions are softmax-normalized cosine affinities to final topic centroids; they are normalized semantic scores rather than calibrated probabilities.',
  },
  lda_aligned_40_v1: {
    id: 'lda_aligned_40_v1',
    label: 'Aligned LDA · 40 lexical topics',
    description: 'A deterministic lexical topic model fitted to the exact same 20,872 BSB passage nodes and text as the BERTopic dataset.',
    calculation: 'Word and bigram counts are modeled with batch variational LDA using 40 topics and seed 42. Each passage is stored as a normalized topic-probability distribution.',
  },
  bertopic_lda_hybrid_v1: {
    id: 'bertopic_lda_hybrid_v1',
    label: 'BERTopic + LDA hybrid',
    description: 'A node-aligned representation that preserves both semantic BERTopic affinities and lexical LDA topic probabilities for every passage.',
    calculation: 'BERTopic and LDA are calculated independently on identical passage IDs and text. Their separately calculated distances are normalized to the same zero-to-one range before weighted fusion.',
  },
};

export const RELATIONSHIP_MODEL_CATALOG = {
  lda_squared_euclidean_knn_legacy: {
    id: 'lda_squared_euclidean_knn_legacy',
    label: 'Legacy LDA topic-distance links',
    metricId: 'squared-euclidean-topic-distribution',
    metricLabel: 'Squared Euclidean topic distance',
    distanceLabel: 'Topic distance',
    scoreDirection: 'lower-is-closer',
    scoreMeaning: 'Lower values mean more similar topic mixtures',
    description: 'Links compare the ten-topic LDA probability mixtures stored for each pair of passages.',
    calculation: 'The edge value is the sum of the squared differences between the passages’ ten topic probabilities. Lower values mean more similar topic mixtures. The legacy source selected approximately ten nearest neighbors per passage; ScriptureScope now renders reciprocal and repeated observations as one undirected edge.',
  },
  lda_jsd_mutual_knn_v1: {
    id: 'lda_jsd_mutual_knn_v1',
    label: 'LDA Jensen–Shannon mutual-neighbor links',
    metricId: 'jensen-shannon-distance-base2-sqrt',
    metricLabel: 'Jensen–Shannon topic distance',
    distanceLabel: 'Topic distance',
    scoreDirection: 'lower-is-closer',
    scoreMeaning: 'Lower values mean more similar topic mixtures',
    description: 'Links compare normalized ten-topic LDA mixtures with a distance designed for probability distributions.',
    calculation: 'All passage pairs are compared with base-2 Jensen–Shannon distance and the square root applied. A link is kept only when both passages rank the other among their ten nearest neighbors, and each undirected pair is stored once.',
  },
  tfidf_cosine_mutual_knn_v1: {
    id: 'tfidf_cosine_mutual_knn_v1',
    label: 'TF-IDF cosine mutual-neighbor links',
    metricId: 'cosine-similarity',
    metricLabel: 'TF-IDF cosine similarity',
    scoreDirection: 'higher-is-closer',
    scoreMeaning: 'Higher values mean more lexical overlap after term weighting',
    description: 'Links compare interpretable lexical vectors and retain relationships that are strong from both passages’ perspectives.',
    calculation: 'Cosine similarity compares the L2-normalized TF-IDF vectors. A link is kept only when both passages rank the other among their ten nearest neighbors. Each edge also stores up to five shared terms that contribute most strongly to the similarity.',
  },
  bertopic_jsd_mutual_knn_v1: {
    id: 'bertopic_jsd_mutual_knn_v1',
    label: 'BERTopic Jensen–Shannon mutual-neighbor links',
    metricId: 'jensen-shannon-distance-base2-sqrt',
    metricLabel: 'BERTopic affinity distance',
    distanceLabel: 'Semantic topic distance',
    scoreDirection: 'lower-is-closer',
    scoreMeaning: 'Lower values mean more similar semantic topic-affinity mixtures',
    description: 'Links compare the 122-dimensional semantic affinity distributions produced by BERTopic.',
    calculation: 'Candidate neighbors are found with seeded NNDescent, reranked with exact base-2 square-root Jensen–Shannon distance, and retained only when both passages place each other in their ten nearest neighbors.',
  },
  lda_aligned_jsd_mutual_knn_v1: {
    id: 'lda_aligned_jsd_mutual_knn_v1',
    label: 'Aligned LDA Jensen–Shannon mutual-neighbor links',
    metricId: 'jensen-shannon-distance-base2-sqrt',
    metricLabel: 'LDA topic distance',
    distanceLabel: 'Lexical topic distance',
    scoreDirection: 'lower-is-closer',
    scoreMeaning: 'Lower values mean more similar lexical topic mixtures',
    description: 'Links compare aligned 40-topic LDA distributions on the canonical BERTopic passage nodes.',
    calculation: 'Candidate neighbors are found with seeded NNDescent, reranked with exact base-2 square-root Jensen–Shannon distance, and retained only for reciprocal top-ten pairs.',
  },
  bertopic_lda_weighted_jsd_v1: {
    id: 'bertopic_lda_weighted_jsd_v1',
    label: 'Balanced BERTopic + LDA links',
    metricId: 'weighted-per-model-jensen-shannon-distance',
    metricLabel: 'Combined semantic and lexical distance',
    distanceLabel: 'Combined distance',
    scoreDirection: 'lower-is-closer',
    scoreMeaning: 'Lower values mean the passages are closer across both semantic and lexical topic models',
    description: 'Links balance transformer-derived semantic affinity with word-derived LDA topic affinity.',
    calculation: 'The edge distance is 50 percent BERTopic Jensen–Shannon distance plus 50 percent LDA Jensen–Shannon distance. Candidate unions from both models are exactly reranked, and only reciprocal top-ten links are retained.',
  },
};

export const LAYOUT_CATALOG = {
  bert_component_preview: {
    id: 'bert_component_preview',
    label: 'Component preview',
    distanceMeaning: 'Screen distance comes from a fallback plot of undocumented feature values and is not a validated semantic distance.',
  },
  lda_topic_anchor_preview: {
    id: 'lda_topic_anchor_preview',
    label: 'Topic-anchor preview',
    distanceMeaning: 'Screen distance comes from a fallback circular projection of topic probabilities and is not the stored relationship distance.',
  },
  drl: {
    id: 'drl',
    label: 'DrL',
    distanceMeaning: 'Screen distance reflects force-directed graph geometry; it is not a calibrated topic distance.',
  },
  kamada_kawai: {
    id: 'kamada_kawai',
    label: 'Kamada–Kawai',
    distanceMeaning: 'Screen positions approximate graph shortest-path distances after spring optimization; they are not the raw edge values.',
  },
  lgl: {
    id: 'lgl',
    label: 'Large Graph Layout',
    distanceMeaning: 'Screen distance reflects a scalable force layout of graph topology; it is not a calibrated topic distance.',
  },
  mds: {
    id: 'mds',
    label: 'Multidimensional Scaling',
    distanceMeaning: 'Screen distance approximates the matrix supplied to MDS, but the legacy pipeline does not document which distance matrix it used.',
  },
  legacy_drl_control: {
    id: 'legacy_drl_control',
    label: 'Controlled DrL coordinates',
    distanceMeaning: 'The existing DrL coordinates are held constant for model comparison; screen distance is not the new relationship score.',
  },
  bertopic_umap_control: {
    id: 'bertopic_umap_control',
    label: 'Controlled BERTopic UMAP',
    distanceMeaning: 'Coordinates are held constant across the BERTopic, aligned LDA, and hybrid views. Nearby screen positions indicate qualitative transformer-embedding similarity, not the displayed link distance.',
  },
  bertopic_lda_3d_projection: {
    id: 'bertopic_lda_3d_projection',
    label: 'Hybrid topic-cloud 3D projection',
    distanceMeaning: 'The existing BERTopic embedding supplies x/y. A deterministic 50/50 projection of BERTopic and aligned LDA topic distributions supplies depth. Link values remain the hybrid Jensen–Shannon distance and are not geometric screen distance.',
  },
};

const createMethod = ({ dataModelId, relationshipModelId = null, layoutId, ...method }) => ({
  ...method,
  dataModelId,
  relationshipModelId,
  layoutId,
  dataModel: DATA_MODEL_CATALOG[dataModelId],
  relationshipModel: relationshipModelId
    ? RELATIONSHIP_MODEL_CATALOG[relationshipModelId]
    : null,
  layout: LAYOUT_CATALOG[layoutId],
  hasRelationships: Boolean(relationshipModelId),
});

export const METHOD_CATALOG = {
  BERT: createMethod({
    id: 'BERT',
    collectionKey: 'BERT',
    label: 'BERT feature preview',
    selectorLabel: 'BERT features · no links',
    type: 'Analysis preview',
    dataModelId: 'bert_legacy_features',
    layoutId: 'bert_component_preview',
    description: 'Shows the legacy BERT-labeled passage features as an unlinked point-cloud preview.',
    calculation: 'ScriptureScope plots the first two stored feature values and colors each passage by its strongest component. This is a display fallback, not a native BERT graph calculation, and this collection contains no relationship links.',
    sources: [{ label: 'Original BERT paper', url: 'https://aclanthology.org/N19-1423/' }],
  }),
  DrL: createMethod({
    id: 'DrL',
    collectionKey: 'DrL',
    label: 'LDA relationships · DrL',
    selectorLabel: 'LDA relationships · DrL',
    type: 'Relationship graph view',
    dataModelId: 'gensim_lda_10_legacy',
    relationshipModelId: 'lda_squared_euclidean_knn_legacy',
    layoutId: 'drl',
    description: 'Displays the legacy LDA topic-distance relationship graph using the scalable DrL force-directed layout.',
    calculation: 'DrL repeatedly moves linked passages together and separates crowded areas through multilevel force-directed optimization. The coordinates are precomputed; changing this layout does not change what a relationship link represents.',
    sources: [{ label: 'igraph DrL documentation', url: 'https://r.igraph.org/reference/layout_with_drl.html' }],
  }),
  lda_jsd_mutual_knn_v1: createMethod({
    id: 'lda_jsd_mutual_knn_v1',
    collectionKey: 'lda_jsd_mutual_knn_v1',
    label: 'LDA relationships · Jensen–Shannon mutual kNN',
    selectorLabel: 'LDA · Jensen–Shannon mutual kNN',
    type: 'Experimental relationship graph',
    dataModelId: 'gensim_lda_10_legacy',
    relationshipModelId: 'lda_jsd_mutual_knn_v1',
    layoutId: 'legacy_drl_control',
    description: 'A controlled comparison that replaces the legacy LDA distance and one-way link rule while keeping coordinates fixed.',
    calculation: 'The view uses exact Jensen–Shannon distance across all passage pairs and retains only mutual ten-nearest-neighbor links. No cutoff or minimum-degree rescue is applied, so passages may remain isolated.',
    sources: [
      { label: 'Gensim LdaModel documentation', url: 'https://radimrehurek.com/gensim/models/ldamodel.html' },
      { label: 'Jensen–Shannon divergence paper', url: 'https://doi.org/10.1109/18.61115' },
    ],
  }),
  tfidf_cosine_mutual_knn_v1: createMethod({
    id: 'tfidf_cosine_mutual_knn_v1',
    collectionKey: 'tfidf_cosine_mutual_knn_v1',
    label: 'Lexical relationships · TF-IDF mutual kNN',
    selectorLabel: 'Lexical · TF-IDF mutual kNN',
    type: 'Experimental relationship graph',
    dataModelId: 'tfidf_lexical_v1',
    relationshipModelId: 'tfidf_cosine_mutual_knn_v1',
    layoutId: 'legacy_drl_control',
    description: 'An interpretable lexical baseline that relates passages through distinctive shared words and exposes evidence for every link.',
    calculation: 'The view compares exact TF-IDF cosine similarity and retains only mutual ten-nearest-neighbor links. Each link records up to five shared terms, while the controlled coordinates remain unchanged for comparison.',
    sources: [{ label: 'Introduction to Information Retrieval', url: 'https://nlp.stanford.edu/IR-book/' }],
  }),
  'bsb-bertopic-linked-v1': createMethod({
    id: 'bsb-bertopic-linked-v1',
    collectionKey: 'bsb-bertopic-linked-v1',
    label: 'BERTopic semantic relationships',
    selectorLabel: 'BERTopic · semantic mutual kNN',
    type: 'Experimental relationship graph',
    expectedCounts: { nodes: 20872, links: 61164 },
    dataModelId: 'bertopic_semantic_affinity_v1',
    relationshipModelId: 'bertopic_jsd_mutual_knn_v1',
    layoutId: 'bertopic_umap_control',
    description: 'The transformer-based BERTopic view with explicit reciprocal nearest-neighbor relationships between passage affinity distributions.',
    calculation: 'The original seeded BERTopic coordinates and reviewed topic labels are retained. Exact Jensen–Shannon distances rerank approximate candidates before reciprocal top-ten link selection.',
    sources: [{ label: 'BERTopic documentation', url: 'https://maartengr.github.io/BERTopic/' }],
  }),
  'bsb-lda-aligned-v1': createMethod({
    id: 'bsb-lda-aligned-v1',
    collectionKey: 'bsb-lda-aligned-v1',
    label: 'Aligned LDA relationships',
    selectorLabel: 'LDA · aligned lexical mutual kNN',
    type: 'Experimental relationship graph',
    expectedCounts: { nodes: 20872, links: 46789 },
    dataModelId: 'lda_aligned_40_v1',
    relationshipModelId: 'lda_aligned_jsd_mutual_knn_v1',
    layoutId: 'bertopic_umap_control',
    description: 'A 40-topic LDA graph rebuilt on exactly the same passage nodes and text as BERTopic for controlled comparison.',
    calculation: 'LDA topic mixtures determine node colors and reciprocal Jensen–Shannon links, while BERTopic coordinates stay fixed so the model can be compared without moving the passages.',
    sources: [{ label: 'scikit-learn LDA documentation', url: 'https://scikit-learn.org/stable/modules/decomposition.html#latentdirichletallocation' }],
  }),
  'bsb-bertopic-lda-hybrid-v1': createMethod({
    id: 'bsb-bertopic-lda-hybrid-v1',
    collectionKey: 'bsb-bertopic-lda-hybrid-v1',
    label: 'Hybrid semantic + lexical relationships',
    selectorLabel: 'Hybrid · BERTopic + LDA',
    type: 'Experimental relationship graph',
    expectedCounts: { nodes: 20872, links: 48570 },
    dataModelId: 'bertopic_lda_hybrid_v1',
    relationshipModelId: 'bertopic_lda_weighted_jsd_v1',
    layoutId: 'bertopic_umap_control',
    description: 'A balanced graph that connects passages supported by transformer semantics, lexical LDA evidence, or both.',
    calculation: 'Each candidate edge keeps its BERTopic and LDA distances separately. Their 50/50 weighted mean determines reciprocal top-ten neighbor selection and the displayed combined distance.',
    sources: [
      { label: 'BERTopic documentation', url: 'https://maartengr.github.io/BERTopic/' },
      { label: 'scikit-learn LDA documentation', url: 'https://scikit-learn.org/stable/modules/decomposition.html#latentdirichletallocation' },
    ],
  }),
  'bsb-bertopic-lda-hybrid-3d-v1': createMethod({
    id: 'bsb-bertopic-lda-hybrid-3d-v1',
    collectionKey: 'bsb-bertopic-lda-hybrid-v1',
    label: 'Hybrid semantic + lexical relationships in 3D',
    selectorLabel: 'Hybrid · 3D / VR node cloud',
    type: 'Immersive relationship graph',
    viewDimension: '3d',
    expectedCounts: { nodes: 20872, links: 48570 },
    dataModelId: 'bertopic_lda_hybrid_v1',
    relationshipModelId: 'bertopic_lda_weighted_jsd_v1',
    layoutId: 'bertopic_lda_3d_projection',
    description: 'An interactive 3D and WebXR view of the same hybrid passage graph, with desktop travel, immersive VR, and permission-gated passthrough AR.',
    calculation: 'The controlled BERTopic x/y coordinates remain unchanged. Depth is a stable equal-weight projection of each passage’s BERTopic and aligned LDA probability vectors, avoiding another dataset build while exposing the hybrid model in three dimensions.',
    sources: [
      { label: 'WebXR Device API', url: 'https://immersive-web.github.io/webxr/' },
      { label: 'BERTopic documentation', url: 'https://maartengr.github.io/BERTopic/' },
    ],
  }),
  Gensim: createMethod({
    id: 'Gensim',
    collectionKey: 'Gensim',
    label: 'LDA topic-mixture preview',
    selectorLabel: 'LDA topics · no links',
    type: 'Analysis preview',
    dataModelId: 'gensim_lda_10_legacy',
    layoutId: 'lda_topic_anchor_preview',
    description: 'Shows each passage’s ten-topic LDA probability mixture as an unlinked point-cloud preview.',
    calculation: 'ScriptureScope projects the ten probabilities around circular topic anchors and colors each passage by its highest-probability topic. This projection is a display fallback rather than part of Gensim LDA, and this collection contains no relationship links.',
    sources: [{ label: 'Gensim LdaModel documentation', url: 'https://radimrehurek.com/gensim/models/ldamodel.html' }],
  }),
  Kamada_Kawai: createMethod({
    id: 'Kamada_Kawai',
    collectionKey: 'Kamada_Kawai',
    label: 'LDA relationships · Kamada–Kawai',
    selectorLabel: 'LDA relationships · Kamada–Kawai',
    type: 'Relationship graph view',
    dataModelId: 'gensim_lda_10_legacy',
    relationshipModelId: 'lda_squared_euclidean_knn_legacy',
    layoutId: 'kamada_kawai',
    description: 'Displays the legacy LDA topic-distance relationship graph using a spring layout based on graph paths.',
    calculation: 'Kamada–Kawai minimizes spring energy so geometric positions approximate graph shortest-path distances. The live collection contains repeated link imports, which ScriptureScope now collapses before rendering.',
    sources: [{ label: 'igraph Kamada–Kawai documentation', url: 'https://igraph.org/c/html/main/igraph-Layout.html#igraph_layout_kamada_kawai' }],
  }),
  Large_Graph_Layout: createMethod({
    id: 'Large_Graph_Layout',
    collectionKey: 'Large_Graph_Layout',
    label: 'LDA relationships · Large Graph Layout',
    selectorLabel: 'LDA relationships · LGL',
    type: 'Relationship graph view',
    dataModelId: 'gensim_lda_10_legacy',
    relationshipModelId: 'lda_squared_euclidean_knn_legacy',
    layoutId: 'lgl',
    description: 'Displays the legacy LDA topic-distance relationship graph using a layout designed for large connected graphs.',
    calculation: 'The layout expands through neighboring graph layers and refines placement with a force-directed cooling process. The coordinates are precomputed and do not redefine the underlying topic-distance links.',
    sources: [{ label: 'igraph LGL documentation', url: 'https://r.igraph.org/reference/layout_with_lgl.html' }],
  }),
  MDS: createMethod({
    id: 'MDS',
    collectionKey: 'MDS',
    label: 'LDA relationships · MDS',
    selectorLabel: 'LDA relationships · MDS',
    type: 'Relationship graph view',
    dataModelId: 'gensim_lda_10_legacy',
    relationshipModelId: 'lda_squared_euclidean_knn_legacy',
    layoutId: 'mds',
    description: 'Displays the legacy LDA topic-distance relationship graph using stored multidimensional-scaling coordinates.',
    calculation: 'Classical MDS derives coordinates from a symmetric distance matrix through eigendecomposition. The legacy generation pipeline is unavailable, so ScriptureScope cannot verify whether these coordinates used raw topic distances or graph shortest-path distances.',
    sources: [{ label: 'igraph MDS documentation', url: 'https://r.igraph.org/reference/layout_with_mds.html' }],
  }),
};

export const getMethodMetadata = (method) => METHOD_CATALOG[method] ?? {
  id: method,
  collectionKey: method,
  label: method,
  selectorLabel: method,
  type: 'Contributed dataset view',
  description: 'No description has been provided for this dataset yet.',
  calculation: 'Calculation details have not been provided. New contributed datasets must include these details in metadata.json.',
  dataModelId: null,
  relationshipModelId: null,
  layoutId: null,
  dataModel: null,
  relationshipModel: null,
  layout: null,
  hasRelationships: false,
  sources: [],
};

const ACTIVE_METHOD_IDS = [
  'bsb-bertopic-linked-v1',
  'bsb-lda-aligned-v1',
  'bsb-bertopic-lda-hybrid-v1',
  'bsb-bertopic-lda-hybrid-3d-v1',
];

export const getAllMethodMetadata = () => (
  ACTIVE_METHOD_IDS.map((id) => METHOD_CATALOG[id])
);

export const getPublishedLocalMethodMetadata = getAllMethodMetadata;
